import { EditorContent } from './elementModelTypes';
import { isTiptapContent } from '../tiptap/utils/editorTypeUtils';
import { mapTiptapNodes } from '../tiptap/utils/jsonContentUtils/mapTiptapNodes';
import { TiptapContent, TiptapContentNode, TiptapMarkType, TiptapNodeType } from '../tiptap/tiptapTypes';
import { compact } from 'lodash';
import { JSONContent } from '@tiptap/core';

type MarkType = NonNullable<JSONContent['marks']>[number];

const mapMark =
    (idMap: Record<string, string>) =>
    (mark: MarkType): MarkType | undefined => {
        if (mark.type !== TiptapMarkType.inlineComment) return mark;

        const commentThreadId = mark.attrs?.commentThreadId;

        if (!commentThreadId) return mark;

        const newId = idMap[commentThreadId];

        // If the new ID is not found, we want to strip the mark, because we never want to
        //  have two cards that reference the same comment thread.
        // This scenario will occur when duplicating a template, because all comment threads
        //  are stripped & thus there won't be a new comment thread ID to map to.
        if (!newId) return undefined;

        return {
            ...mark,
            attrs: {
                ...mark.attrs,
                commentThreadId: newId,
            },
        };
    };

/**
 * If the node is a text node with inline comment marks, this function will
 * change the ID of the inline comment marks to match the provided ID map.
 */
const mapTiptapNodeOnDuplication =
    (idMap: Record<string, string>) =>
    (node?: TiptapContentNode): TiptapContentNode => {
        if (!node) return {};

        if (node.type !== TiptapNodeType.text) return node;

        if (!node.marks || node.marks.length === 0) return node;

        const updatedMarks = compact(node.marks.map(mapMark(idMap)));

        return {
            ...node,
            marks: updatedMarks,
        };
    };

/**
 * Find all inline comment marks in the text & change their attribute to match the
 */
export const createDuplicateTextContent = (
    textContent: EditorContent,
    idMap: Record<string, string>,
): EditorContent => {
    if (!isTiptapContent(textContent)) return textContent;
    return mapTiptapNodes(textContent, mapTiptapNodeOnDuplication(idMap)) as TiptapContent;
};
